import { useState, useCallback, useEffect } from 'react';
import { Search, Plus, RefreshCw, Check, Loader2, AlertCircle, Server, Camera as CameraIcon, Trash2, Shield, Settings, ChevronDown, ChevronRight, Radar, ArrowLeft, Building2, MapPin, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  probeDevice,
  registerDiscoveredChannels,
  fetchSnapshot,
  getRegisteredVpnCameras,
  getRegisteredVpnCamerasForAccount,
  cleanupOrphanStreams,
  getStreamChannels,
  getStreamCapabilities,
  configureStream,
  scanNetwork,
  deleteCameraById,
  deleteCamerasByIds,
  type RegisteredCameraInfo,
  type StreamChannelInfo,
  type StreamCapabilities,
  type ScannedDevice,
  type ScanResult,
  type RegisterConflict,
} from '@/services/cameras';
import { fetchAccounts, fetchSitesByAccount } from '@/services/accounts';
import { fetchAllGateways, fetchGatewayForSite } from '@/services/provisioning';
import { supabase } from '@/services/supabase';
import { Pagination, usePagination } from '@/components/Pagination';
import { cn } from '@/lib/utils';
import type { Account, AlarmSite, ProbeResult, SiteGateway } from '@asi/shared';

// ─── Device Discovery Tab ─────────────────────────────────

/**
 * Wrapper around `registerDiscoveredChannels` that surfaces serial-collision
 * conflicts to the operator via a confirmation dialog. When the gateway
 * reports that one or more channels are already linked to an active camera
 * elsewhere (typically the same physical device that was previously imported
 * at another site/account), we prompt the user to either:
 *   • Cancel — leave the existing registrations untouched.
 *   • Confirm — call again with `force: true`, which disables the existing
 *     rows and frees the streamId so the new location takes ownership.
 */
async function registerWithConflictPrompt(
  params: Parameters<typeof registerDiscoveredChannels>[0],
): Promise<Awaited<ReturnType<typeof registerDiscoveredChannels>>> {
  const result = await registerDiscoveredChannels(params);
  if (result.conflicts.length === 0) return result;

  const lines = result.conflicts.map((c: RegisterConflict) => {
    const where = c.existingSiteName
      ? `"${c.existingSiteName}"`
      : c.existingHost
        ? `IP ${c.existingHost}`
        : 'otra ubicación';
    return `  • Canal ${c.channelNo} → ya registrada como "${c.existingName}" en ${where}`;
  }).join('\n');

  const message =
    `Esta cámara ya está registrada en otro sitio o cuenta:\n\n${lines}\n\n` +
    `¿Deseas desvincularla de la ubicación anterior y moverla aquí?\n\n` +
    `(Al confirmar, las cámaras anteriores se deshabilitarán automáticamente. ` +
    `Esta acción no elimina su historial.)`;

  if (!window.confirm(message)) return result;

  return registerDiscoveredChannels({ ...params, force: true });
}

/**
 * Given a CIDR like "10.0.0.48/28", returns the usable host range in the last octet.
 * Assumes prefix >= 24 (all VPN subnets fit within the last octet).
 */
function cidrHostRange(cidr: string): { startHost: number; endHost: number } | null {
  const match = cidr.match(/^(\d+\.\d+\.\d+\.(\d+))\/(\d+)$/);
  if (!match) return null;
  const lastOctet = parseInt(match[2], 10);
  const prefix = parseInt(match[3], 10);
  if (prefix < 24 || prefix > 30) return null;
  const blockSize = Math.pow(2, 32 - prefix);
  return { startHost: lastOctet + 1, endHost: lastOctet + blockSize - 2 };
}

function DiscoveryPanel({ accountId = null, sites = [], onRegistered, fixedSite = null }: {
  accountId?: string | null;
  sites?: AlarmSite[];
  onRegistered?: () => void;
  fixedSite?: AlarmSite | null;
}) {
  const [host, setHost] = useState('');
  const [hostError, setHostError] = useState('');
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('');
  const [onvifPort, setOnvifPort] = useState('80');
  const [siteId, setSiteId] = useState<string>(fixedSite?.id ?? '');
  const [probing, setProbing] = useState(false);
  const [probeResult, setProbeResult] = useState<ProbeResult | null>(null);
  const [error, setError] = useState('');

  // Subnet scan state
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);

  // Site VPN gateway (loaded when siteId changes)
  const [siteGateway, setSiteGateway] = useState<SiteGateway | null>(null);
  useEffect(() => {
    if (!siteId) { setSiteGateway(null); return; }
    fetchGatewayForSite(siteId).then((gw) => {
      setSiteGateway(gw);
      // Auto-fill subnet: extract first 3 octets from vpn_subnet (e.g. "10.0.0.48/28" → "10.0.0")
      if (gw?.vpn_subnet) {
        const threeOctets = gw.vpn_subnet.split('/')[0].split('.').slice(0, 3).join('.');
        setHost(threeOctets);
      }
    }).catch(() => setSiteGateway(null));
  }, [siteId]);

  // Detect input mode: 3 octets = subnet scan, 4 octets = single probe
  const isSubnet = (v: string) => /^\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(v.trim());
  const isValidIp = (v: string) => /^((25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(25[0-5]|2[0-4]\d|[01]?\d\d?)$/.test(v.trim());
  const inputMode: 'subnet' | 'single' | 'partial' = isSubnet(host) ? 'subnet' : isValidIp(host) ? 'single' : 'partial';

  // Single-screen flow: one button scans the whole site subnet; a specific IP is an optional extra.
  const [showManual, setShowManual] = useState(false);
  const [resultFilter, setResultFilter] = useState<'all' | 'new' | 'registered' | 'auth' | 'other'>('all');
  const siteRange = siteGateway?.vpn_subnet ? cidrHostRange(siteGateway.vpn_subnet) : null;

  // Registration state
  const [selectedChannels, setSelectedChannels] = useState<Set<number>>(new Set());
  const [namePrefix, setNamePrefix] = useState('');
  const [customNames, setCustomNames] = useState<Record<number, string>>({});
  const [registering, setRegistering] = useState(false);
  const [registerResult, setRegisterResult] = useState<{
    registered: Array<{ cameraId: string; name: string; channelNo: number; streamId: string; registrationCode: string }>;
  } | null>(null);

  // Bulk import state
  const [bulkImporting, setBulkImporting] = useState(false);

  // Snapshot state: channelNo → objectURL
  const [snapshots, setSnapshots] = useState<Record<number, string>>({});
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);

  // Stream config state
  const [streamChannels, setStreamChannels] = useState<StreamChannelInfo[]>([]);
  const [expandedConfig, setExpandedConfig] = useState<number | null>(null); // channelNo
  const [capabilities, setCapabilities] = useState<Record<number, StreamCapabilities>>({});
  const [loadingCaps, setLoadingCaps] = useState<number | null>(null);
  const [configuring, setConfiguring] = useState<number | null>(null);
  const [configError, setConfigError] = useState('');
  const [configSuccess, setConfigSuccess] = useState('');

  // Registered cameras state (used only for duplicate detection during probing)
  const [registeredCameras, setRegisteredCameras] = useState<RegisteredCameraInfo[]>([]);
  const [loadingRegistered, setLoadingRegistered] = useState(false);

  // Load registered cameras on mount and after registration (for duplicate detection)
  const loadRegisteredCameras = useCallback(async () => {
    setLoadingRegistered(true);
    try {
      const cameras = accountId
        ? await getRegisteredVpnCamerasForAccount(accountId)
        : await getRegisteredVpnCameras();
      setRegisteredCameras(cameras);
    } catch {
      // non-critical
    } finally {
      setLoadingRegistered(false);
    }
  }, [accountId]);

  useEffect(() => {
    loadRegisteredCameras();
  }, [loadRegisteredCameras]);

  // Duplicate detection: channels already registered for current host
  const registeredChannelMap = useCallback(() => {
    if (!host) return new Map<number, RegisteredCameraInfo>();
    const map = new Map<number, RegisteredCameraInfo>();
    for (const cam of registeredCameras) {
      if (cam.host === host && cam.channelNo != null) {
        map.set(cam.channelNo, cam);
      }
    }
    return map;
  }, [host, registeredCameras]);

  const handleCleanup = useCallback(async () => {
    try {
      await cleanupOrphanStreams();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al limpiar streams');
    }
  }, []);

  /** Update scan result locally after per-device import (no full re-scan). */
  const markDeviceChannelsImported = useCallback((deviceHost: string, channelNos: number[]) => {
    setScanResult((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        devices: prev.devices.map((dev) => {
          if (dev.host !== deviceHost) return dev;
          const updatedChannels = dev.channels.map((ch) =>
            channelNos.includes(ch.channelNo) ? { ...ch, imported: true } : ch,
          );
          const allImported = updatedChannels.every((ch) => ch.imported);
          return { ...dev, channels: updatedChannels, imported: allImported };
        }),
      };
    });
  }, []);

  /** Import all available channels from all unimported devices in one batch. */
  const handleImportAllAvailable = useCallback(async () => {
    const available = scanResult?.devices.filter((d) => !d.imported && d.device) ?? [];
    if (available.length === 0) return;
    setBulkImporting(true);
    setError('');
    for (const dev of available) {
      const channelNos = dev.channels.filter((ch) => !ch.imported).map((ch) => ch.channelNo);
      if (channelNos.length === 0) continue;
      try {
        await registerWithConflictPrompt({
          host: dev.host,
          username,
          password,
          onvifPort: dev.httpPort,
          siteId: siteId || undefined,
          channels: channelNos,
        });
        markDeviceChannelsImported(dev.host, channelNos);
        loadRegisteredCameras();
        onRegistered?.();
      } catch (err) {
        setError(`Error importando ${dev.host}: ${err instanceof Error ? err.message : 'Error desconocido'}`);
      }
    }
    setBulkImporting(false);
  }, [scanResult, username, password, siteId, markDeviceChannelsImported, loadRegisteredCameras, onRegistered]);

  const handleProbe = useCallback(async () => {
    if (!host || !password) return;

    // ── Subnet scan mode ──────────────────────────────────
    if (inputMode === 'subnet') {
      setProbing(true);
      setError('');
      setProbeResult(null);
      setScanResult(null);
      try {
        const cidrRange = siteGateway?.vpn_subnet ? cidrHostRange(siteGateway.vpn_subnet) : null;
        const res = await scanNetwork({ subnet: host.trim(), username, password, ...cidrRange ?? {} });
        setScanResult(res);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al escanear subred');
      } finally {
        setProbing(false);
      }
      return;
    }

    // ── Single device probe mode ───────────────────────────
    if (!isValidIp(host)) {
      setHostError('IP incompleta — ingresa 4 octetos (ej: 10.0.1.10) o 3 para escanear la subred');
      return;
    }
    setHostError('');
    setProbing(true);
    setError('');
    setProbeResult(null);
    setScanResult(null);
    setRegisterResult(null);

    // Revoke old snapshot URLs
    Object.values(snapshots).forEach((url) => URL.revokeObjectURL(url));
    setSnapshots({});

    try {
      const result = await probeDevice({
        host,
        username,
        password,
        onvifPort: parseInt(onvifPort) || 80,
      });
      setProbeResult(result);
      // Select all NON-duplicate channels by default
      const duplicates = new Map<number, RegisteredCameraInfo>();
      for (const cam of registeredCameras) {
        if (cam.host === host && cam.channelNo != null) {
          duplicates.set(cam.channelNo, cam);
        }
      }
      setSelectedChannels(new Set(
        result.channels.filter((ch) => !duplicates.has(ch.channelNo)).map((ch) => ch.channelNo),
      ));

      // Load snapshots in the background
      setSnapshots({});
      setLoadingSnapshots(true);
      const snapshotPromises = result.channels.map(async (ch) => {
        try {
          const url = await fetchSnapshot({
            host,
            username,
            password,
            channelNo: ch.channelNo,
          });
          setSnapshots((prev) => ({ ...prev, [ch.channelNo]: url }));
        } catch {
          // Snapshot failed for this channel — not critical
        }
      });
      Promise.all(snapshotPromises).finally(() => setLoadingSnapshots(false));

      // Load stream channels (detailed config) in the background
      setStreamChannels([]);
      setExpandedConfig(null);
      setCapabilities({});
      getStreamChannels({
        host,
        username,
        password,
        port: parseInt(onvifPort) || 80,
      }).then((res) => setStreamChannels(res.channels))
        .catch(() => { /* non-critical — probe already succeeded */ });

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al descubrir dispositivo');
    } finally {
      setProbing(false);
    }
  }, [host, username, password, onvifPort]);

  /** Scan every address of the site VPN subnet — no IP typing needed. */
  const handleScanSite = useCallback(async () => {
    if (!siteGateway || !password) return;
    const prefix = siteGateway.vpn_subnet.split('/')[0].split('.').slice(0, 3).join('.');
    setShowManual(false);
    setProbing(true);
    setError('');
    setProbeResult(null);
    setScanResult(null);
    setRegisterResult(null);
    setResultFilter('all');
    try {
      setScanResult(await scanNetwork({ subnet: prefix, username, password, ...(cidrHostRange(siteGateway.vpn_subnet) ?? {}) }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al escanear la red del sitio');
    } finally {
      setProbing(false);
    }
  }, [siteGateway, username, password]);

  const toggleChannel = (ch: number) => {
    setSelectedChannels((prev) => {
      const next = new Set(prev);
      if (next.has(ch)) next.delete(ch);
      else next.add(ch);
      return next;
    });
  };

  /** Expand/collapse stream config for a channel; load capabilities on first expand. */
  const handleToggleConfig = useCallback(async (channelId: number) => {
    if (expandedConfig === channelId) {
      setExpandedConfig(null);
      return;
    }
    setExpandedConfig(channelId);
    setConfigError('');
    setConfigSuccess('');

    // Load capabilities if not cached
    if (!capabilities[channelId]) {
      setLoadingCaps(channelId);
      try {
        const res = await getStreamCapabilities({
          host,
          username,
          password,
          port: parseInt(onvifPort) || 80,
          channelId,
        });
        setCapabilities((prev) => ({ ...prev, [channelId]: res.capabilities }));
      } catch (err) {
        setConfigError(err instanceof Error ? err.message : 'Error al obtener opciones');
      } finally {
        setLoadingCaps(null);
      }
    }
  }, [expandedConfig, capabilities, host, username, password, onvifPort]);

  /** Apply stream configuration changes to a channel. */
  const handleApplyConfig = useCallback(async (channelId: number, config: {
    width?: number;
    height?: number;
    codec?: string;
    fps?: number;
    bitrate?: number;
  }) => {
    setConfiguring(channelId);
    setConfigError('');
    setConfigSuccess('');
    try {
      const result = await configureStream({
        host,
        username,
        password,
        port: parseInt(onvifPort) || 80,
        channelId,
        config,
      });

      // Update the local stream channels with new config
      if (result.after) {
        setStreamChannels((prev) =>
          prev.map((ch) => ch.id === channelId ? result.after! : ch),
        );
      }
      setConfigSuccess(`Canal ${channelId} configurado: ${result.after?.width}×${result.after?.height}`);
    } catch (err) {
      setConfigError(err instanceof Error ? err.message : 'Error al configurar stream');
    } finally {
      setConfiguring(null);
    }
  }, [host, username, password, onvifPort]);

  const handleRegister = useCallback(async () => {
    if (!probeResult || selectedChannels.size === 0) return;
    setRegistering(true);
    setError('');

    try {
      const result = await registerWithConflictPrompt({
        host,
        username,
        password,
        onvifPort: parseInt(onvifPort) || 80,
        siteId: siteId || undefined,
        channels: [...selectedChannels],
        namePrefix: namePrefix || undefined,
        names: Object.keys(customNames).length > 0
          ? Object.fromEntries(Object.entries(customNames).map(([k, v]) => [Number(k), v]))
          : undefined,
      });
      setRegisterResult(result);
      // Refresh registered cameras list (for duplicate detection) + notify parent
      loadRegisteredCameras();
      onRegistered?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al registrar cámaras');
    } finally {
      setRegistering(false);
    }
  }, [probeResult, selectedChannels, host, username, password, onvifPort, siteId, namePrefix, customNames, loadRegisteredCameras, onRegistered]);

  return (
    <div className="space-y-6">
      {/* ── Controles: red del sitio + credenciales + acción principal ── */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Red del sitio</p>
              {siteGateway ? (
                <p className="mt-0.5 text-sm">
                  <span className="font-mono">{siteGateway.vpn_subnet}</span>
                  <span className="text-muted-foreground"> · router {siteGateway.vpn_gateway_ip}</span>
                  {siteRange && <span className="text-muted-foreground"> · {siteRange.endHost - siteRange.startHost + 1} direcciones</span>}
                </p>
              ) : (
                <p className="mt-0.5 text-sm text-amber-600">
                  Este sitio no tiene gateway VPN: solo se puede probar una IP específica.
                </p>
              )}
            </div>
            {siteGateway && (
              <Badge
                variant="outline"
                className={cn('text-xs font-normal', (GATEWAY_STATE[siteGateway.status] ?? GATEWAY_STATE.provisioned).className)}
              >
                {(GATEWAY_STATE[siteGateway.status] ?? GATEWAY_STATE.provisioned).label}
              </Badge>
            )}
          </div>

          {!fixedSite && sites.length > 0 && (
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">Sitio de destino</label>
              <select
                value={siteId}
                onChange={(e) => setSiteId(e.target.value)}
                className="h-9 w-full max-w-sm rounded-md border border-input bg-background px-3 py-1 text-sm"
              >
                <option value="">— Sin asignar —</option>
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end">
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">Usuario del NVR / cámara</label>
              <Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="admin" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">Contraseña del NVR / cámara</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && siteGateway && password && !probing) handleScanSite(); }}
              />
            </div>
            {siteGateway && (
              <Button size="lg" onClick={handleScanSite} disabled={probing || !password} className="gap-2">
                {probing && !showManual ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radar className="h-4 w-4" />}
                {probing && !showManual ? 'Escaneando…' : 'Escanear red del sitio'}
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Son las credenciales del equipo instalado en el sitio (no las de ASI). No se guardan.
          </p>

          {/* Modo manual: una IP concreta (o una subred escrita a mano) */}
          {siteGateway && (
            <button
              type="button"
              onClick={() => setShowManual((v) => !v)}
              className="flex items-center gap-1 text-xs text-primary hover:underline"
            >
              <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', showManual && 'rotate-180')} />
              Probar una IP específica
            </button>
          )}
          {(showManual || !siteGateway) && (
            <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
              <div className="grid gap-3 md:grid-cols-[minmax(0,2fr)_6rem_auto] md:items-end">
                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-1">IP del equipo</label>
                  <Input
                    placeholder="10.0.0.18"
                    value={host}
                    onChange={(e) => { setHost(e.target.value); setHostError(''); setScanResult(null); setProbeResult(null); }}
                    onBlur={() => {
                      if (host && inputMode === 'partial')
                        setHostError('IP incompleta — 4 octetos (10.0.0.18) o 3 para escanear una subred (10.0.0)');
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !probing && host && password && inputMode !== 'partial') handleProbe(); }}
                    className={hostError ? 'border-destructive' : ''}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-1">Puerto</label>
                  <Input placeholder="80" value={onvifPort} onChange={(e) => setOnvifPort(e.target.value)} />
                </div>
                <Button variant="outline" onClick={handleProbe} disabled={probing || !host || !password || inputMode === 'partial'}>
                  {probing && showManual ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                  {inputMode === 'subnet' ? 'Escanear subred' : 'Probar'}
                </Button>
              </div>
              <p className={cn('text-xs', hostError ? 'text-destructive' : 'text-muted-foreground')}>
                {hostError || 'Útil para revisar un equipo puntual, o si está fuera del rango de la red del sitio.'}
              </p>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-destructive text-sm">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Escaneando ── */}
      {probing && (
        <Card>
          <CardContent className="py-10 text-center">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
            <p className="mt-3 font-medium">
              {inputMode === 'single' && showManual ? `Consultando ${host}…` : 'Buscando NVRs y cámaras en la red del sitio…'}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">Puede tardar hasta un minuto.</p>
          </CardContent>
        </Card>
      )}

      {/* ── Resultados del escaneo ── */}
      {scanResult && !probing && (() => {
        const found = scanResult.devices.filter((d) => d.device);
        const newDevs = found.filter((d) => !d.imported);
        const registeredDevs = found.filter((d) => d.imported);
        const authDevs = scanResult.devices.filter((d) => !d.device && d.authError);
        const otherDevs = scanResult.devices.filter((d) => !d.device && !d.authError);
        const newChannels = newDevs.reduce((n, d) => n + d.channels.filter((ch) => !ch.imported).length, 0);
        const chips: { id: typeof resultFilter; label: string; count: number; tone?: string }[] = [
          { id: 'all', label: 'Todos', count: scanResult.devices.length },
          { id: 'new', label: 'Nuevos', count: newDevs.length, tone: 'text-amber-600' },
          { id: 'registered', label: 'Ya registrados', count: registeredDevs.length, tone: 'text-green-600' },
          { id: 'auth', label: 'Clave incorrecta', count: authDevs.length, tone: 'text-red-600' },
          { id: 'other', label: 'Otros equipos', count: otherDevs.length },
        ];
        const show = (id: typeof resultFilter) => resultFilter === 'all' || resultFilter === id;
        return (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-1.5">
                  {chips.filter((c) => c.id === 'all' || c.count > 0).map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setResultFilter(c.id)}
                      className={cn(
                        'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                        resultFilter === c.id ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:bg-muted',
                      )}
                    >
                      {c.label} <span className={cn(resultFilter !== c.id && c.tone)}>{c.count}</span>
                    </button>
                  ))}
                </div>
                {newChannels > 0 && (
                  <Button onClick={handleImportAllAvailable} disabled={bulkImporting} className="gap-2">
                    {bulkImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    {bulkImporting ? 'Registrando…' : `Registrar todo lo nuevo (${newChannels} ${newChannels === 1 ? 'cámara' : 'cámaras'})`}
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Se revisaron {scanResult.scanned} direcciones de {scanResult.subnet} en {(scanResult.elapsed / 1000).toFixed(1)} s ·
                {' '}{scanResult.alive} respondieron
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              {scanResult.devices.length === 0 && (
                <div className="rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
                  <p className="font-medium text-foreground">No se encontró ningún equipo.</p>
                  <ul className="mt-2 list-disc space-y-1 pl-5">
                    <li>¿El gateway VPN del sitio está en línea?</li>
                    <li>¿Los NVR y cámaras están conectados a los puertos de cámaras del MikroTik (ether2–ether4) o a su WiFi?</li>
                    <li>¿Recibieron IP del MikroTik (dentro de {siteGateway?.vpn_subnet ?? 'la red del sitio'})?</li>
                  </ul>
                </div>
              )}

              {show('new') && newDevs.map((d) => (
                <DeviceScanCard key={d.host} device={d} scanCredentials={{ username, password }} siteId={siteId || undefined} defaultExpanded onImported={(channelNos) => { markDeviceChannelsImported(d.host, channelNos); onRegistered?.(); }} />
              ))}
              {show('registered') && registeredDevs.map((d) => (
                <DeviceScanCard key={d.host} device={d} scanCredentials={{ username, password }} siteId={siteId || undefined} onImported={(channelNos) => markDeviceChannelsImported(d.host, channelNos)} />
              ))}
              {show('auth') && authDevs.length > 0 && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3">
                  <p className="text-sm font-medium text-red-600 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" /> Usuario o contraseña incorrectos
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {authDevs.map((d) => <Badge key={d.host} variant="outline" className="font-mono text-xs">{d.host}</Badge>)}
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Estos equipos respondieron pero rechazaron las credenciales. Si tienen otra contraseña, cámbiala arriba y vuelve a escanear.
                  </p>
                </div>
              )}
              {show('other') && otherDevs.length > 0 && (
                <div className="rounded-lg border border-border p-3">
                  <p className="text-sm font-medium text-muted-foreground">Otros equipos en la red</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {otherDevs.map((d) => <Badge key={d.host} variant="outline" className="font-mono text-xs text-muted-foreground">{d.host}</Badge>)}
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Respondieron pero no son NVR ni cámaras compatibles (routers, switches, paneles u otros).
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Probe Results */}
      {probeResult && !registerResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              {probeResult.device.deviceType === 'nvr' ? (
                <Server className="h-5 w-5" />
              ) : (
                <CameraIcon className="h-5 w-5" />
              )}
              {probeResult.device.manufacturer} {probeResult.device.model}
              <Badge variant="outline" className="ml-2">
                {probeResult.device.discoveryMethod.toUpperCase()}
              </Badge>
              <span className="text-sm font-normal text-muted-foreground ml-auto">
                {probeResult.elapsed}ms
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 text-sm">
              <div>
                <span className="text-muted-foreground">Serial:</span>{' '}
                <span className="font-mono">{probeResult.device.serialNumber || '—'}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Tipo:</span>{' '}
                {probeResult.device.deviceType === 'nvr' ? 'NVR' : probeResult.device.deviceType === 'camera' ? 'Cámara' : 'Desconocido'}
              </div>
              <div>
                <span className="text-muted-foreground">Firmware:</span>{' '}
                {probeResult.device.firmwareVersion || '—'}
              </div>
              <div>
                <span className="text-muted-foreground">Canales:</span>{' '}
                {probeResult.channels.length}
              </div>
            </div>

            {/* Name prefix */}
            <div className="mb-4">
              <label className="text-sm text-muted-foreground mb-1 block">
                Prefijo de nombre (ej: nombre del sitio)
              </label>
              <Input
                placeholder="Ej: Oficina Central"
                value={namePrefix}
                onChange={(e) => setNamePrefix(e.target.value)}
                className="max-w-xs"
              />
            </div>

            {/* Channel list */}
            <div className="space-y-2">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium">Canales encontrados</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (selectedChannels.size === probeResult.channels.length) {
                      setSelectedChannels(new Set());
                    } else {
                      setSelectedChannels(new Set(probeResult.channels.map((ch) => ch.channelNo)));
                    }
                  }}
                >
                  {selectedChannels.size === probeResult.channels.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
                </Button>
              </div>

              {probeResult.channels.map((ch) => {
                const duplicates = registeredChannelMap();
                const existingCam = duplicates.get(ch.channelNo);
                // Find sub-stream info from stream channels for this physical channel
                const subChannels = streamChannels.filter(
                  (sc) => sc.channelNo === ch.channelNo && sc.streamType === 'sub',
                );
                const mainChannels = streamChannels.filter(
                  (sc) => sc.channelNo === ch.channelNo && sc.streamType === 'main',
                );
                return (
                <div key={ch.channelNo} className="space-y-0">
                  <div
                    className={`flex items-center gap-4 p-3 rounded-lg border cursor-pointer transition-colors ${
                      existingCam
                        ? 'border-yellow-400/50 bg-yellow-50/50 dark:bg-yellow-950/10'
                        : selectedChannels.has(ch.channelNo)
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-muted-foreground/30'
                    } ${subChannels.some((sc) => expandedConfig === sc.id) ? 'rounded-b-none' : ''}`}
                    onClick={() => !existingCam && toggleChannel(ch.channelNo)}
                  >
                    <div className={`h-5 w-5 rounded border flex items-center justify-center ${
                      existingCam
                        ? 'bg-yellow-400 border-yellow-400 text-white'
                        : selectedChannels.has(ch.channelNo) ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/30'
                    }`}>
                      {existingCam ? <Check className="h-3 w-3" /> : selectedChannels.has(ch.channelNo) && <Check className="h-3 w-3" />}
                    </div>

                    {existingCam && (
                      <Badge variant="outline" className="text-[10px] border-yellow-400 text-yellow-700 dark:text-yellow-400 shrink-0">
                        Ya registrada: {existingCam.name}
                      </Badge>
                    )}

                    {/* Snapshot thumbnail */}
                    <div className="shrink-0 w-24 h-16 rounded overflow-hidden bg-muted flex items-center justify-center">
                      {snapshots[ch.channelNo] ? (
                        <img
                          src={snapshots[ch.channelNo]}
                          alt={`CH${ch.channelNo}`}
                          className="w-full h-full object-cover"
                        />
                      ) : loadingSnapshots ? (
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      ) : (
                        <CameraIcon className="h-5 w-5 text-muted-foreground/40" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <Input
                        value={customNames[ch.channelNo] ?? ch.name}
                        onChange={(e) => {
                          e.stopPropagation();
                          setCustomNames((prev) => ({ ...prev, [ch.channelNo]: e.target.value }));
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="h-8 text-sm"
                        placeholder={ch.name}
                      />
                    </div>

                    <Badge variant="secondary" className="font-mono text-xs">
                      CH{ch.channelNo}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {ch.codec}
                    </Badge>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {ch.resolution} • {ch.fps}fps
                    </span>
                    <div className="flex gap-1">
                      {ch.hasMainStream && <Badge className="text-[10px] px-1">Main</Badge>}
                      {ch.hasSubStream && <Badge variant="outline" className="text-[10px] px-1">Sub</Badge>}
                    </div>

                    {/* Stream config button */}
                    {subChannels.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 shrink-0"
                        onClick={(e) => { e.stopPropagation(); handleToggleConfig(subChannels[0].id); }}
                        title="Configurar sub-stream"
                      >
                        <Settings className={`h-3.5 w-3.5 transition-transform ${expandedConfig === subChannels[0].id ? 'rotate-90 text-primary' : ''}`} />
                      </Button>
                    )}
                  </div>

                  {/* Expanded stream configuration */}
                  {subChannels.map((sc) => expandedConfig === sc.id && (
                    <StreamConfigPanel
                      key={sc.id}
                      channel={sc}
                      mainChannel={mainChannels[0] ?? null}
                      capabilities={capabilities[sc.id] ?? null}
                      loadingCaps={loadingCaps === sc.id}
                      configuring={configuring === sc.id}
                      onApply={(config) => handleApplyConfig(sc.id, config)}
                      error={configError}
                      success={configSuccess}
                    />
                  ))}
                </div>
                );
              })}
            </div>

            {/* Register button */}
            <div className="mt-6 flex justify-end">
              <Button
                onClick={handleRegister}
                disabled={registering || selectedChannels.size === 0}
                size="lg"
              >
                {registering ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                Registrar {selectedChannels.size} cámara{selectedChannels.size !== 1 ? 's' : ''}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Registration Result */}
      {registerResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg text-green-600">
              <Check className="h-5 w-5" />
              {registerResult.registered.length} cámara{registerResult.registered.length !== 1 ? 's' : ''} registrada{registerResult.registered.length !== 1 ? 's' : ''}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {registerResult.registered.map((cam) => (
                <div key={cam.cameraId} className="flex items-center gap-4 p-3 rounded-lg border bg-green-50 dark:bg-green-950/20">
                  <CameraIcon className="h-5 w-5 text-green-600" />
                  <div className="flex-1">
                    <div className="font-medium">{cam.name}</div>
                    <div className="text-xs text-muted-foreground">
                      Stream: <span className="font-mono">{cam.streamId}</span>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-[10px] text-muted-foreground uppercase">Código QR</div>
                    <div className="font-mono text-lg font-bold tracking-wider">{cam.registrationCode}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 flex gap-2">
              <Button variant="outline" onClick={() => { setProbeResult(null); setRegisterResult(null); }}>
                <Plus className="h-4 w-4 mr-2" />
                Descubrir otro dispositivo
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DeviceScanCard({ device, scanCredentials, siteId, onImported, defaultExpanded }: {
  device: ScannedDevice;
  scanCredentials: { username: string; password: string };
  siteId?: string;
  onImported: (importedChannelNos: number[]) => void;
  defaultExpanded?: boolean;
}) {
  const isNvr = device.device?.deviceType === 'nvr';
  const [expanded, setExpanded] = useState(defaultExpanded ?? false);
  const [selectedChannels, setSelectedChannels] = useState<Set<number>>(new Set());
  const [channelNames, setChannelNames] = useState<Record<number, string>>({});
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [importResult, setImportResult] = useState<{ registered: Array<{ name: string; channelNo: number; registrationCode: string }> } | null>(null);

  // Snapshot state
  const [snapshots, setSnapshots] = useState<Record<number, string>>({});
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);

  // Available (not-yet-imported) channels
  const availableChannels = device.channels.filter((ch) => !ch.imported);
  const importedChannels = device.channels.filter((ch) => ch.imported);

  // Auto-select all available channels when the card is expanded
  useEffect(() => {
    if (expanded && availableChannels.length > 0 && selectedChannels.size === 0) {
      setSelectedChannels(new Set(availableChannels.map((ch) => ch.channelNo)));
    }
  }, [expanded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load snapshots when expanded
  useEffect(() => {
    if (!expanded || device.channels.length === 0 || loadingSnapshots || Object.keys(snapshots).length > 0) return;

    let cancelled = false;
    setLoadingSnapshots(true);

    (async () => {
      const results: Record<number, string> = {};
      // Load snapshots in parallel (max 4 concurrent)
      const channels = [...device.channels];
      const batchSize = 4;
      for (let i = 0; i < channels.length; i += batchSize) {
        if (cancelled) break;
        const batch = channels.slice(i, i + batchSize);
        const settled = await Promise.allSettled(
          batch.map(async (ch) => {
            const url = await fetchSnapshot({
              host: device.host,
              username: scanCredentials.username,
              password: scanCredentials.password,
              channelNo: ch.channelNo,
            });
            return { channelNo: ch.channelNo, url };
          }),
        );
        for (const res of settled) {
          if (res.status === 'fulfilled') {
            results[res.value.channelNo] = res.value.url;
          }
        }
        if (!cancelled) setSnapshots({ ...results });
      }
      if (!cancelled) setLoadingSnapshots(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [expanded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      for (const url of Object.values(snapshots)) {
        URL.revokeObjectURL(url);
      }
    };
  }, [snapshots]);

  const toggleChannel = (channelNo: number) => {
    setSelectedChannels((prev) => {
      const next = new Set(prev);
      if (next.has(channelNo)) next.delete(channelNo);
      else next.add(channelNo);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedChannels(new Set(availableChannels.map((ch) => ch.channelNo)));
  };

  const selectNone = () => {
    setSelectedChannels(new Set());
  };

  const handleImport = async () => {
    if (selectedChannels.size === 0) return;
    setImporting(true);
    setImportError('');
    setImportResult(null);

    try {
      const names: Record<number, string> = {};
      for (const [chNo, name] of Object.entries(channelNames)) {
        if (name.trim()) names[Number(chNo)] = name.trim();
      }

      const result = await registerWithConflictPrompt({
        host: device.host,
        username: scanCredentials.username,
        password: scanCredentials.password,
        onvifPort: device.httpPort,
        siteId: siteId || undefined,
        channels: Array.from(selectedChannels),
        names: Object.keys(names).length > 0 ? names : undefined,
      });

      setImportResult(result);
      onImported(Array.from(selectedChannels));
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Error al importar');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Device header */}
      <div
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/30 transition-colors"
        onClick={() => !importResult && setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${device.imported ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
            {isNvr ? <Server className="h-5 w-5" /> : <CameraIcon className="h-5 w-5" />}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium">
                {device.device ? `${device.device.manufacturer} ${device.device.model}` : device.host}
              </span>
              <Badge variant={isNvr ? 'default' : 'secondary'} className="text-[10px]">
                {device.device?.deviceType?.toUpperCase() ?? 'DESCONOCIDO'}
              </Badge>
            </div>
            <div className="text-xs text-muted-foreground flex gap-4 mt-0.5">
              <span className="font-mono">{device.host}</span>
              {device.device?.serialNumber && <span>SN: {device.device.serialNumber}</span>}
              {device.device?.firmwareVersion && <span>FW: {device.device.firmwareVersion}</span>}
              {device.channelCount > 0 && (
                <span>
                  {device.channelCount} canal{device.channelCount !== 1 ? 'es' : ''}
                  {importedChannels.length > 0 && ` (${importedChannels.length} importado${importedChannels.length !== 1 ? 's' : ''})`}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {device.imported ? (
            <Badge variant="outline" className="text-green-600 border-green-200">
              <Check className="h-3 w-3 mr-1" />
              Todos importados
            </Badge>
          ) : importResult ? (
            <Badge variant="outline" className="text-green-600 border-green-200">
              <Check className="h-3 w-3 mr-1" />
              {importResult.registered.length} importado{importResult.registered.length !== 1 ? 's' : ''}
            </Badge>
          ) : (
            <>
              <Badge variant="outline" className="text-amber-600 border-amber-200">
                {availableChannels.length} disponible{availableChannels.length !== 1 ? 's' : ''}
              </Badge>
              <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </>
          )}
        </div>
      </div>

      {/* Expanded channel selection */}
      {expanded && !importResult && (
        <div className="border-t bg-muted/20 p-4 space-y-3">
          {/* Select all / none */}
          {availableChannels.length > 1 && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">Seleccionar:</span>
              <button onClick={selectAll} className="text-primary hover:underline">Todos ({availableChannels.length})</button>
              <span className="text-muted-foreground">|</span>
              <button onClick={selectNone} className="text-primary hover:underline">Ninguno</button>
            </div>
          )}

          {/* Channel grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {device.channels.map((ch) => {
              const isSelected = selectedChannels.has(ch.channelNo);
              return (
                <div
                  key={ch.channelNo}
                  className={`group relative rounded-lg border overflow-hidden transition-all ${
                    ch.imported
                      ? 'opacity-50 pointer-events-none'
                      : isSelected
                        ? 'ring-2 ring-primary border-primary'
                        : 'hover:border-foreground/30 cursor-pointer'
                  }`}
                  onClick={() => !ch.imported && toggleChannel(ch.channelNo)}
                >
                  {/* Snapshot area */}
                  <div className="aspect-video bg-muted relative">
                    {snapshots[ch.channelNo] ? (
                      <img
                        src={snapshots[ch.channelNo]}
                        alt={`Canal ${ch.channelNo}`}
                        className="w-full h-full object-cover"
                      />
                    ) : loadingSnapshots ? (
                      <div className="w-full h-full flex items-center justify-center">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <CameraIcon className="h-6 w-6 text-muted-foreground/40" />
                      </div>
                    )}

                    {/* Top-left: checkbox or imported check */}
                    <div className="absolute top-1.5 left-1.5">
                      {ch.imported ? (
                        <div className="bg-green-500 text-white rounded-full p-0.5">
                          <Check className="h-3 w-3" />
                        </div>
                      ) : (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleChannel(ch.channelNo)}
                          onClick={(e) => e.stopPropagation()}
                          className="h-4 w-4 rounded border-2 border-white/80 shadow-sm cursor-pointer"
                        />
                      )}
                    </div>

                    {/* Top-right: codec badge */}
                    <div className="absolute top-1.5 right-1.5">
                      <span className="text-[9px] font-mono bg-black/60 text-white px-1.5 py-0.5 rounded">
                        {ch.codec.toUpperCase()} {ch.width}×{ch.height}
                      </span>
                    </div>
                  </div>

                  {/* Channel info + name input */}
                  <div className="p-2 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-muted-foreground">Canal {ch.channelNo}</span>
                      {ch.imported && (
                        <span className="text-[10px] text-green-600 font-medium">
                          {ch.cameraName ?? 'Importado'}
                        </span>
                      )}
                    </div>
                    {!ch.imported && (
                      <Input
                        placeholder={`Nombre para esta cámara`}
                        value={channelNames[ch.channelNo] ?? ''}
                        onChange={(e) => setChannelNames((prev) => ({ ...prev, [ch.channelNo]: e.target.value }))}
                        className="h-7 text-xs"
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Import button */}
          <div className="flex items-center justify-between pt-1">
            <div className="text-xs text-muted-foreground">
              {selectedChannels.size > 0
                ? `${selectedChannels.size} de ${availableChannels.length} seleccionado${selectedChannels.size !== 1 ? 's' : ''}`
                : 'Haz clic en una cámara para seleccionarla'}
            </div>
            <Button
              size="sm"
              onClick={handleImport}
              disabled={selectedChannels.size === 0 || importing}
              className="gap-2"
            >
              {importing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              {importing ? 'Importando...' : `Importar ${selectedChannels.size > 0 ? `(${selectedChannels.size})` : ''}`}
            </Button>
          </div>

          {importError && (
            <div className="flex items-center gap-2 text-destructive text-xs">
              <AlertCircle className="h-3.5 w-3.5" />
              {importError}
            </div>
          )}
        </div>
      )}

      {/* Import success result */}
      {importResult && (
        <div className="border-t bg-green-50 p-4">
          <div className="flex items-center gap-2 text-green-700 text-sm font-medium mb-2">
            <Check className="h-4 w-4" />
            {importResult.registered.length} cámara{importResult.registered.length !== 1 ? 's' : ''} importada{importResult.registered.length !== 1 ? 's' : ''}
          </div>
          <div className="space-y-1">
            {importResult.registered.map((cam) => (
              <div key={cam.channelNo} className="text-xs text-green-700 flex gap-2">
                <span>Canal {cam.channelNo}</span>
                <span className="font-medium">{cam.name}</span>
                <span className="text-green-500 font-mono">QR: {cam.registrationCode}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Stream Config Panel (expandable per channel) ─────────

function StreamConfigPanel({
  channel,
  mainChannel,
  capabilities,
  loadingCaps,
  configuring,
  onApply,
  error,
  success,
}: {
  channel: StreamChannelInfo;
  mainChannel: StreamChannelInfo | null;
  capabilities: StreamCapabilities | null;
  loadingCaps: boolean;
  configuring: boolean;
  onApply: (config: { width?: number; height?: number; codec?: string; fps?: number; bitrate?: number }) => void;
  error: string;
  success: string;
}) {
  const [selectedRes, setSelectedRes] = useState(`${channel.width}x${channel.height}`);
  const [selectedCodec, setSelectedCodec] = useState(channel.codec);
  const [selectedFps, setSelectedFps] = useState(String(channel.fps));

  // Update local state when channel data changes (after apply)
  useEffect(() => {
    setSelectedRes(`${channel.width}x${channel.height}`);
    setSelectedCodec(channel.codec);
    setSelectedFps(String(channel.fps));
  }, [channel.width, channel.height, channel.codec, channel.fps]);

  const hasChanges =
    selectedRes !== `${channel.width}x${channel.height}` ||
    selectedCodec !== channel.codec ||
    selectedFps !== String(channel.fps);

  const handleApply = () => {
    const config: { width?: number; height?: number; codec?: string; fps?: number } = {};
    const [w, h] = selectedRes.split('x').map(Number);
    if (w !== channel.width || h !== channel.height) {
      config.width = w;
      config.height = h;
    }
    if (selectedCodec !== channel.codec) config.codec = selectedCodec;
    if (Number(selectedFps) !== channel.fps) config.fps = Number(selectedFps);
    onApply(config);
  };

  return (
    <div className="border border-t-0 rounded-b-lg bg-muted/30 p-4 space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <Settings className="h-4 w-4" />
        Configuración Sub-Stream — Canal {channel.channelNo}
      </div>

      {/* Current config */}
      <div className="flex gap-6 text-xs">
        <div>
          <span className="text-muted-foreground">Actual:</span>{' '}
          <span className="font-mono">{channel.codec.toUpperCase()} {channel.width}×{channel.height} {channel.fps}fps {channel.bitrate > 0 ? `${channel.bitrate}kbps` : ''}</span>
        </div>
        {mainChannel && (
          <div>
            <span className="text-muted-foreground">Main:</span>{' '}
            <span className="font-mono">{mainChannel.codec.toUpperCase()} {mainChannel.width}×{mainChannel.height} {mainChannel.fps}fps</span>
          </div>
        )}
      </div>

      {loadingCaps ? (
        <div className="flex items-center gap-2 py-2">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Cargando opciones...</span>
        </div>
      ) : capabilities && capabilities.resolutions.length > 0 ? (
        <div className="flex items-end gap-4 flex-wrap">
          {/* Resolution */}
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Resolución</label>
            <select
              value={selectedRes}
              onChange={(e) => setSelectedRes(e.target.value)}
              className="h-8 rounded-md border border-input bg-background px-2 py-1 text-sm"
            >
              {capabilities.resolutions.map((r) => (
                <option key={`${r.width}x${r.height}`} value={`${r.width}x${r.height}`}>
                  {r.width}×{r.height}
                </option>
              ))}
            </select>
          </div>

          {/* Codec */}
          {capabilities.codecs.length > 1 && (
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Códec</label>
              <select
                value={selectedCodec}
                onChange={(e) => setSelectedCodec(e.target.value)}
                className="h-8 rounded-md border border-input bg-background px-2 py-1 text-sm"
              >
                {capabilities.codecs.map((c) => (
                  <option key={c} value={c}>{c.toUpperCase()}</option>
                ))}
              </select>
            </div>
          )}

          {/* FPS */}
          {capabilities.fpsRange.max > 0 && (
            <div>
              <label className="text-xs text-muted-foreground block mb-1">FPS</label>
              <Input
                type="number"
                value={selectedFps}
                onChange={(e) => setSelectedFps(e.target.value)}
                min={capabilities.fpsRange.min > 0 ? Math.ceil(capabilities.fpsRange.min / 100) : 1}
                max={Math.floor(capabilities.fpsRange.max / 100)}
                className="h-8 w-20 text-sm"
              />
            </div>
          )}

          {/* Apply button */}
          <Button
            size="sm"
            onClick={handleApply}
            disabled={!hasChanges || configuring}
            className="h-8"
          >
            {configuring ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
            ) : (
              <Check className="h-3.5 w-3.5 mr-1" />
            )}
            Aplicar
          </Button>
        </div>
      ) : capabilities ? (
        <div className="text-xs text-muted-foreground bg-muted/50 rounded-md p-3">
          <p className="font-medium mb-1">Este dispositivo no soporta configuración remota de streams.</p>
          <p>Use la calidad intermedia (Mid 720p) en el visor para un balance entre calidad y ancho de banda.</p>
        </div>
      ) : null}

      {error && (
        <div className="flex items-center gap-2 text-destructive text-xs">
          <AlertCircle className="h-3.5 w-3.5" />
          {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 text-green-600 text-xs">
          <Check className="h-3.5 w-3.5" />
          {success}
        </div>
      )}
    </div>
  );
}

// ─── Registered Cameras Panel (account-scoped) ────────────

const _registeredCamerasCache = new Map<string, RegisteredCameraInfo[]>();

function RegisteredCamerasPanel({ accountId, onCountChange }: { accountId: string; onCountChange?: (count: number) => void }) {
  const [cameras, setCameras] = useState<RegisteredCameraInfo[]>(_registeredCamerasCache.get(accountId) ?? []);
  const [loading, setLoading] = useState(!_registeredCamerasCache.has(accountId));
  const [cleaningUp, setCleaningUp] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<{ removed: string[]; kept: string[] } | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteAllConfirm, setDeleteAllConfirm] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);

  const load = useCallback(async () => {
    if (!_registeredCamerasCache.has(accountId)) setLoading(true);
    try {
      const cams = await getRegisteredVpnCamerasForAccount(accountId);
      _registeredCamerasCache.set(accountId, cams);
      setCameras(cams);
      onCountChange?.(cams.length);
    } catch {
      // non-critical
    } finally {
      setLoading(false);
    }
  }, [accountId, onCountChange]);

  useEffect(() => { load(); }, [load]);

  const handleCleanup = useCallback(async () => {
    setCleaningUp(true);
    setCleanupResult(null);
    try {
      const result = await cleanupOrphanStreams();
      setCleanupResult(result);
      await load();
    } catch {
      // non-critical
    } finally {
      setCleaningUp(false);
    }
  }, [load]);

  const handleDeleteOne = useCallback(async (cam: RegisteredCameraInfo) => {
    if (!window.confirm(`¿Eliminar la cámara "${cam.name}"${cam.siteName ? ` de ${cam.siteName}` : ''}?\n\nSe borra su registro y su stream de video. No se puede deshacer.`)) return;
    setDeletingId(cam.id);
    try {
      await deleteCameraById(cam.id);
      await load();
    } finally {
      setDeletingId(null);
    }
  }, [load]);

  const handleDeleteAll = useCallback(async () => {
    setDeletingAll(true);
    try {
      const ids = cameras.map((c) => c.id);
      if (ids.length > 0) await deleteCamerasByIds(ids);
      await cleanupOrphanStreams().catch(() => {});
      await load();
      setDeleteAllConfirm(false);
    } finally {
      setDeletingAll(false);
    }
  }, [cameras, load]);

  const online = cameras.filter((c) => !c.isDisabled && c.status === 'ONLINE').length;
  const offline = cameras.filter((c) => !c.isDisabled && c.status !== 'ONLINE').length;
  const disabled = cameras.filter((c) => c.isDisabled).length;
  const bySite = cameras.reduce<Record<string, RegisteredCameraInfo[]>>((acc, c) => {
    const key = c.siteName ?? 'Sin sitio';
    (acc[key] ??= []).push(c);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-medium flex items-center gap-2">
            <Shield className="h-5 w-5 text-muted-foreground" />
            Cámaras VPN de esta cuenta
            <Badge variant="secondary">{cameras.length}</Badge>
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            <span className="text-green-600">{online} en línea</span> · {offline} fuera de línea
            {disabled > 0 && <> · {disabled} deshabilitada{disabled !== 1 ? 's' : ''}</>}
            {' '}· el estado se revisa cada 2 minutos
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} title="Volver a leer la lista y los estados">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
            Actualizar lista
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCleanup}
            disabled={cleaningUp}
            title="Mantenimiento del servidor de video: quita streams que ya no tienen cámara en el sistema. NO elimina cámaras."
          >
            {cleaningUp ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Settings className="h-3.5 w-3.5 mr-1" />}
            Limpiar streams sin uso
          </Button>
          {cameras.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => setDeleteAllConfirm(true)} disabled={deletingAll} className="text-destructive border-destructive/40 hover:bg-destructive/10">
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              Eliminar todas
            </Button>
          )}
        </div>
      </div>

      {/* Delete-all confirmation */}
      {deleteAllConfirm && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4 space-y-3">
          <p className="text-sm font-medium text-destructive">
            ¿Eliminar las {cameras.length} cámaras VPN de esta cuenta?
          </p>
          <p className="text-xs text-muted-foreground">
            Borra sus registros y sus streams de video en {Object.keys(bySite).join(', ')}. No se puede deshacer.
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="destructive" onClick={handleDeleteAll} disabled={deletingAll}>
              {deletingAll ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
              Sí, eliminar todas
            </Button>
            <Button size="sm" variant="outline" onClick={() => setDeleteAllConfirm(false)} disabled={deletingAll}>
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {cleanupResult && (
        <div className="p-3 rounded-lg border border-blue-500/30 bg-blue-500/5 text-sm">
          {cleanupResult.removed.length > 0
            ? `Se quitaron ${cleanupResult.removed.length} streams de video sin cámara. ${cleanupResult.kept.length} streams siguen en uso.`
            : `No había streams sin uso. ${cleanupResult.kept.length} streams en uso.`}
          <span className="text-muted-foreground"> (Esta limpieza no elimina cámaras.)</span>
        </div>
      )}

      {cameras.length === 0 && !loading ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          Esta cuenta no tiene cámaras VPN registradas. Usa <strong>Descubrir</strong> en uno de sus sitios.
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(bySite).map(([siteName, cams]) => (
            <Card key={siteName}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  {siteName}
                  <Badge variant="secondary" className="text-[10px]">{cams.length}</Badge>
                </CardTitle>
              </CardHeader>
              <div className="divide-y divide-border">
                {cams.map((cam) => (
                  <div key={cam.id} className={cn('flex items-center gap-3 px-6 py-2.5 text-sm', cam.isDisabled && 'opacity-60')}>
                    <CameraIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{cam.name}</p>
                      <p className="text-xs text-muted-foreground font-mono">
                        {cam.host}{cam.channelNo != null ? ` · canal ${cam.channelNo}` : ''}
                      </p>
                    </div>
                    {cam.registrationCode && (
                      <Badge variant="outline" className="hidden md:inline-flex font-mono text-[10px] tracking-wider" title="Código para vincular desde la app">
                        {cam.registrationCode}
                      </Badge>
                    )}
                    {cam.isDisabled ? (
                      <Badge variant="secondary" className="text-[10px]">Deshabilitada</Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className={cn('text-[10px]', cam.status === 'ONLINE'
                          ? 'border-green-500/30 bg-green-500/10 text-green-600'
                          : 'text-muted-foreground')}
                      >
                        {cam.status === 'ONLINE' ? 'En línea' : 'Fuera de línea'}
                      </Badge>
                    )}
                    <button
                      onClick={() => handleDeleteOne(cam)}
                      disabled={deletingId === cam.id}
                      className="ml-1 p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
                      title="Eliminar cámara"
                    >
                      {deletingId === cam.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Account Picker ───────────────────────────────────────

const ACCOUNT_TYPE_LABEL: Record<string, string> = {
  residential: 'Residencial',
  commercial:  'Comercial',
  corporate:   'Corporativo',
  government:  'Gobierno',
};

let _cachedPickerAccounts: Account[] | null = null;

function AccountPicker({ onSelect }: { onSelect: (account: Account) => void }) {
  const [accounts, setAccounts] = useState<Account[]>(_cachedPickerAccounts ?? []);
  const [loading, setLoading] = useState(_cachedPickerAccounts === null);
  const [siteCounts, setSiteCounts] = useState<Record<string, number>>({});
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    if (_cachedPickerAccounts === null) setLoading(true);
    fetchAccounts()
      .then((data) => { _cachedPickerAccounts = data; setAccounts(data); })
      .catch(() => {})
      .finally(() => setLoading(false));
    fetchSiteCountsByAccount().then(setSiteCounts).catch(() => {});
  }, []);

  const q = search.trim().toLowerCase();
  const visible = accounts
    .filter((a) => showInactive || a.is_active)
    .filter((a) => !q || a.name.toLowerCase().includes(q) || a.account_number.toLowerCase().includes(q))
    // Accounts with sites first (the ones you can actually work on), then by name.
    .sort((a, b) => Number((siteCounts[b.id] ?? 0) > 0) - Number((siteCounts[a.id] ?? 0) > 0) || a.name.localeCompare(b.name));
  const pager = usePagination(visible, `${q}|${showInactive}`, 25);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold">Registro de dispositivos</h1>
        <p className="text-muted-foreground mt-1">
          Elige una cuenta y luego el sitio donde quieres descubrir cámaras y NVRs por la VPN.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar cuenta por nombre o número..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="h-3.5 w-3.5" />
          Mostrar inactivas
        </label>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : visible.length === 0 ? (
        <p className="py-16 text-center text-sm text-muted-foreground">No hay cuentas que coincidan.</p>
      ) : (
        <Card>
          <div className="divide-y divide-border">
            {pager.pageItems.map((acc) => {
              const sites = siteCounts[acc.id] ?? 0;
              return (
                <button
                  key={acc.id}
                  onClick={() => onSelect(acc)}
                  className={cn(
                    'w-full flex items-center gap-4 px-5 py-3 text-left transition-colors hover:bg-muted/40',
                    sites === 0 && 'opacity-60',
                  )}
                >
                  <div className="p-2 rounded-lg bg-muted shrink-0">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate">{acc.name}</span>
                      <Badge variant="outline" className="font-mono text-[10px]">{acc.account_number}</Badge>
                      <Badge variant="secondary" className="text-[10px]">{ACCOUNT_TYPE_LABEL[acc.type] ?? acc.type}</Badge>
                      {!acc.is_active && <Badge variant="secondary" className="text-[10px]">Inactiva</Badge>}
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {sites === 0 ? 'Sin sitios' : `${sites} ${sites === 1 ? 'sitio' : 'sitios'}`}
                  </span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </button>
              );
            })}
          </div>
          <Pagination
            className="border-t border-border px-5 py-3"
            page={pager.page}
            pageCount={pager.pageCount}
            pageSize={pager.pageSize}
            total={pager.total}
            onPageChange={pager.setPage}
            onPageSizeChange={pager.setPageSize}
            itemLabel="cuentas"
          />
        </Card>
      )}
    </div>
  );
}

/** Number of sites per account id (one lightweight query). */
async function fetchSiteCountsByAccount(): Promise<Record<string, number>> {
  const { data, error } = await supabase.from('alarm_sites').select('account_id').not('account_id', 'is', null);
  if (error) throw error;
  const counts: Record<string, number> = {};
  for (const { account_id } of (data ?? []) as { account_id: string }[]) counts[account_id] = (counts[account_id] ?? 0) + 1;
  return counts;
}

// ─── Account Detail View ──────────────────────────────────

const GATEWAY_STATE: Record<string, { label: string; className: string }> = {
  online: { label: 'VPN en línea', className: 'bg-green-500/10 text-green-600 border-green-500/30' },
  offline: { label: 'VPN fuera de línea', className: 'bg-amber-500/10 text-amber-600 border-amber-500/30' },
  provisioned: { label: 'VPN sin conectar', className: 'bg-blue-500/10 text-blue-600 border-blue-500/30' },
  error: { label: 'VPN con error', className: 'bg-red-500/10 text-red-600 border-red-500/30' },
};

function AccountDetailView({ account, onBack }: { account: Account; onBack: () => void }) {
  const [sites, setSites] = useState<AlarmSite[]>([]);
  const [gateways, setGateways] = useState<Record<string, SiteGateway>>({});
  const [loadingSites, setLoadingSites] = useState(true);
  const [activeTab, setActiveTab] = useState('sites');
  const [discoveringSite, setDiscoveringSite] = useState<AlarmSite | null>(null);
  const [registeredCount, setRegisteredCount] = useState(0);
  const [search, setSearch] = useState('');
  const [onlyWithGateway, setOnlyWithGateway] = useState(false);

  useEffect(() => {
    fetchSitesByAccount(account.id)
      .then(setSites)
      .catch(() => setSites([]))
      .finally(() => setLoadingSites(false));
    fetchAllGateways()
      .then((rows) => setGateways(Object.fromEntries(rows.map((g) => [g.site_id, g]))))
      .catch(() => setGateways({}));
    getRegisteredVpnCamerasForAccount(account.id)
      .then((cams) => setRegisteredCount(cams.length))
      .catch(() => {});
  }, [account.id]);

  const q = search.trim().toLowerCase();
  const visibleSites = sites
    .filter((s) => !onlyWithGateway || gateways[s.id])
    .filter((s) => !q || s.name.toLowerCase().includes(q) || (s.address ?? '').toLowerCase().includes(q))
    // Sites with a VPN gateway first: those are the only ones where discovery works.
    .sort((a, b) => Number(!!gateways[b.id]) - Number(!!gateways[a.id]) || a.name.localeCompare(b.name));
  const withGatewayCount = sites.filter((s) => gateways[s.id]).length;
  const pager = usePagination(visibleSites, `${q}|${onlyWithGateway}`, 25);

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 flex-wrap text-sm">
        <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Cuentas
        </Button>
        <span className="text-muted-foreground">/</span>
        {discoveringSite ? (
          <button onClick={() => setDiscoveringSite(null)} className="font-medium hover:underline">{account.name}</button>
        ) : (
          <span className="font-semibold">{account.name}</span>
        )}
        <Badge variant="outline" className="font-mono text-xs">{account.account_number}</Badge>
        {discoveringSite && (
          <>
            <span className="text-muted-foreground">/</span>
            <span className="font-semibold">{discoveringSite.name}</span>
          </>
        )}
      </div>

      {/* Discovery sub-view (when a site is selected for discovery) */}
      {discoveringSite ? (
        <DiscoveryPanel
          accountId={account.id}
          fixedSite={discoveringSite}
          onRegistered={() => {
            getRegisteredVpnCamerasForAccount(account.id)
              .then((cams) => setRegisteredCount(cams.length))
              .catch(() => {});
          }}
        />
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="sites" className="gap-2">
              <Building2 className="h-4 w-4" />
              Sitios
              <Badge variant="secondary" className="ml-1 text-[10px]">{sites.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="cameras" className="gap-2">
              <CameraIcon className="h-4 w-4" />
              Cámaras registradas
              {registeredCount > 0 && (
                <Badge variant="secondary" className="ml-1 text-[10px]">{registeredCount}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="sites" className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[220px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar sitio por nombre o dirección..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                <input type="checkbox" checked={onlyWithGateway} onChange={(e) => setOnlyWithGateway(e.target.checked)} className="h-3.5 w-3.5" />
                Solo con gateway VPN ({withGatewayCount})
              </label>
            </div>

            {loadingSites ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : sites.length === 0 ? (
              <div className="text-center py-12 text-sm text-muted-foreground">
                Esta cuenta no tiene sitios registrados.
              </div>
            ) : visibleSites.length === 0 ? (
              <div className="text-center py-12 text-sm text-muted-foreground">No hay sitios que coincidan.</div>
            ) : (
              <Card>
                <div className="divide-y divide-border">
                  {pager.pageItems.map((site) => {
                    const gw = gateways[site.id];
                    const state = gw ? (GATEWAY_STATE[gw.status] ?? GATEWAY_STATE.provisioned) : null;
                    return (
                      <div key={site.id} className="flex items-center gap-4 px-5 py-3">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm truncate">{site.name}</p>
                          {site.address && (
                            <p className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground">
                              <MapPin className="h-3 w-3 shrink-0" />
                              <span className="truncate">{site.address}</span>
                            </p>
                          )}
                        </div>
                        {gw && state ? (
                          <div className="hidden sm:flex flex-col items-end shrink-0">
                            <Badge variant="outline" className={cn('text-[10px] font-normal', state.className)}>{state.label}</Badge>
                            <span className="mt-0.5 font-mono text-[10px] text-muted-foreground">{gw.vpn_subnet}</span>
                          </div>
                        ) : (
                          <span className="hidden sm:inline text-xs text-muted-foreground shrink-0">Sin gateway VPN</span>
                        )}
                        <Button
                          size="sm"
                          variant={gw ? 'default' : 'outline'}
                          className="gap-1.5 shrink-0"
                          disabled={!gw}
                          title={gw
                            ? 'Buscar cámaras y NVRs en la red de este sitio'
                            : 'Este sitio no tiene gateway MikroTik: provisiónalo en el sitio → pestaña Gateway'}
                          onClick={() => setDiscoveringSite(site)}
                        >
                          <Search className="h-3.5 w-3.5" />
                          Descubrir
                        </Button>
                      </div>
                    );
                  })}
                </div>
                <Pagination
                  className="border-t border-border px-5 py-3"
                  page={pager.page}
                  pageCount={pager.pageCount}
                  pageSize={pager.pageSize}
                  total={pager.total}
                  onPageChange={pager.setPage}
                  onPageSizeChange={pager.setPageSize}
                  itemLabel="sitios"
                />
              </Card>
            )}
          </TabsContent>

          <TabsContent value="cameras">
            <RegisteredCamerasPanel accountId={account.id} onCountChange={setRegisteredCount} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────

export function DeviceManagerPage() {
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);

  return (
    <div className="p-6">
      {selectedAccount ? (
        <AccountDetailView
          account={selectedAccount}
          onBack={() => setSelectedAccount(null)}
        />
      ) : (
        <AccountPicker onSelect={setSelectedAccount} />
      )}
    </div>
  );
}
